/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, call, cancel, delay, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ConsumptionMeta,
  ConsumptionAction,
  ActionRequest,
  XError,
  BookmarksErrors,
  BookmarksConfigs,
  Specs,
  Seconds,
  BookmarksApi,
  BookmarksPhoenixApi,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import { Selectors as PlayerSelectors, ActionCreators as PlayerActionCreators } from '../../../../store/ducks/player';

export enum ActionTypes {
  SET_REQUEST = 'consumption/bookmarks/SET_REQUEST',
  HIT_TASK_START = 'consumption/bookmarks/HIT_TASK_START',
  HIT_TASK_STOP = 'consumption/bookmarks/HIT_TASK_STOP',
}

interface Meta extends ConsumptionMeta {
  action: ConsumptionAction;
}

type SetRequest = ActionRequest<ActionTypes.SET_REQUEST, Meta>;
type HitTaskStart = ActionRequest<ActionTypes.HIT_TASK_START>;
type HitTaskStop = ActionRequest<ActionTypes.HIT_TASK_STOP>;

export type HitTaskActionTypes = HitTaskStart | HitTaskStop;

const Actions = {
  setRequest: (meta: Meta): SetRequest => ({ type: ActionTypes.SET_REQUEST, meta }),
  hitTaskStart: (): HitTaskStart => ({ type: ActionTypes.HIT_TASK_START }),
  hitTaskStop: (): HitTaskStop => ({ type: ActionTypes.HIT_TASK_STOP }),
};

export const Creators = {
  setBookmark: (meta: Meta): SetRequest => Actions.setRequest(meta),
};

// REDUCER
export interface ReducerState {
  hitTaskRunning: boolean;
}

const initialState: ReducerState = {
  hitTaskRunning: false,
};

export default function bookmarks(state = initialState, action: HitTaskActionTypes): ReducerState {
  switch (action.type) {
    case ActionTypes.HIT_TASK_START:
      return {
        hitTaskRunning: true,
      };
    case ActionTypes.HIT_TASK_STOP:
      return {
        hitTaskRunning: false,
      };
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  hitTaskRunning: (state: StoreState): boolean => state.consumption.bookmarks.hitTaskRunning,
};

function* handleConcurrentErrors(error: XError, action: ConsumptionAction) {
  // TODO handle concurrency errors

  /* const notification: PassiveNotificationMeta = {
    title: translate['passiveNotification.bookmarks.concurrence.error.title'],
    description: error.translation,
    icon: Icons.ERROR,
    iconColor: Colors.brand,
  }; */
  yield 1;
  if (action === ConsumptionAction.HIT && error.code === BookmarksErrors.API.concurrent) {
    // TODO check if it should be a passive notification or a modal screen
    // yield put(PassiveNotificationsActionCreators.setNotification(notification));
    yield put(PlayerActionCreators.pauseRequest());
  }
}

function* hitRegisterSaga(meta: ConsumptionMeta): SagaIterator {
  const { isLive } = meta;
  const configs: BookmarksConfigs | undefined = yield select(Ducks.Dms.Selectors.getBookmarks);

  const mediaHit = isLive
    ? configs?.linearMediaHit || Specs.DMS_LINEAR_MEDIA_HIT
    : configs?.vodMediaHit || Specs.DMS_VOD_MEDIA_HIT;

  try {
    while (true) {
      yield delay(mediaHit * 1000);
      const location: Seconds = yield select(PlayerSelectors.currentProgress);
      yield put(Creators.setBookmark({ ...meta, action: ConsumptionAction.HIT, location: Math.floor(location) }));
    }
  } finally {
    // player paused or stopped
  }
}

function* startHitRegister(meta: ConsumptionMeta): SagaIterator {
  const isSsmEnabled: boolean = yield select(Ducks.Dms.Selectors.isSsmEnabled);
  const hitTaskRunning = yield select(Selectors.hitTaskRunning);
  if (!isSsmEnabled && !hitTaskRunning) {
    yield put(Actions.hitTaskStart());
    const assetLoadingTask = yield fork(hitRegisterSaga, meta);
    yield take(ActionTypes.HIT_TASK_STOP);
    yield cancel(assetLoadingTask);
  }
}

// SAGAS
function* sendBookmarkSaga(meta: Meta): SagaIterator {
  const { isLive, asset, channel, fileId, action, location, averageBitRate, totalBitRate, currentBitRate } = meta;
  let bookmarkAsset = asset;
  let programId: number | undefined;
  if (isLive) {
    bookmarkAsset = channel;
    programId = asset?.id;
  }
  try {
    const isSsmEnabled: boolean = yield select(Ducks.Dms.Selectors.isSsmEnabled);
    const setBookmark = isSsmEnabled ? BookmarksPhoenixApi.setBookmark : BookmarksApi.setBookmark;

    yield Ducks.Api.callApi(
      setBookmark,
      bookmarkAsset,
      fileId,
      action,
      location,
      programId,
      averageBitRate,
      totalBitRate,
      currentBitRate
    );

    // update bookmarks cache only when one of these actions are triggered
    if (asset && (action === ConsumptionAction.STOP || action === ConsumptionAction.HIT)) {
      yield call(Ducks.BookmarksCache.updateBookmarksSaga, asset, location);
    }
  } catch (error) {
    yield call(handleConcurrentErrors, XError.fromError(error), action);
  }
}

function* setBookmarkSaga(requestAction: SetRequest): SagaIterator {
  const { meta } = requestAction;
  const { action } = meta;

  if (action === ConsumptionAction.PLAY || action === ConsumptionAction.FIRST_PLAY) {
    yield fork(startHitRegister, meta);
  } else if (action === ConsumptionAction.PAUSE || action === ConsumptionAction.STOP) {
    yield put(Actions.hitTaskStop());
  }

  yield call(sendBookmarkSaga, meta);
}

export function* saga(): SagaIterator {
  yield all([takeEvery(ActionTypes.SET_REQUEST, setBookmarkSaga)]);
}
